const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function sendErrorToBackend(payload) {
  try {
    // keepalive lets the request survive page unload. No sendBeacon: it always sends
    // credentials, which the server's wildcard CORS (Allow-Origin: *) rejects.
    const data = JSON.stringify({ timestamp: Date.now(), userAgent: navigator.userAgent, ...payload });
    fetch(`${API_URL}/log-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {}); // silently ignore network errors in the logger itself
  } catch (_) {
    // never throw from error logger
  }
}

export function logError(message, { stack, url, type = 'error', extra } = {}) {
  sendErrorToBackend({ type, message: String(message), stack, url: url || window.location.href, extra });
}

// Benign browser warning (layout settled across frames) – not a real error
const isResizeObserverNoise = (message) =>
  typeof message === 'string' && message.includes('ResizeObserver loop');

// Axios-Fehler enthalten die komplette Request-Config inkl. Authorization-Header –
// nur das Nötige loggen, damit keine Login-Tokens in Logs/DB landen
const redact = (value) => {
  if (value && value.isAxiosError) {
    return {
      message: value.message,
      status: value.response?.status,
      method: value.config?.method,
      url: `${value.config?.baseURL || ''}${value.config?.url || ''}`,
      response: value.response?.data,
    };
  }
  return value;
};

export function initGlobalErrorLogging() {
  // Swallow ResizeObserver noise before the dev overlay or any other listener sees it
  window.addEventListener('error', (event) => {
    if (isResizeObserverNoise(event.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }, true);

  // Catch uncaught JS errors (including minify crashes)
  window.onerror = (message, source, lineno, colno, error) => {
    if (isResizeObserverNoise(message)) return true;
    logError(message, {
      type: 'window.onerror',
      stack: error?.stack,
      url: source,
      extra: { lineno, colno },
    });
    return false; // don't suppress default browser behaviour
  };

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    logError(reason?.message || String(reason), {
      type: 'unhandledrejection',
      stack: reason?.stack,
    });
  });

  // Intercept console.error so every console.error also reaches the backend
  const originalConsoleError = console.error.bind(console);
  console.error = (...args) => {
    originalConsoleError(...args);
    try {
      const message = args.map(a => (typeof a === 'object' ? JSON.stringify(redact(a)) : String(a))).join(' ');
      const stack = args.find(a => a instanceof Error)?.stack;
      logError(message, { type: 'console.error', stack });
    } catch (_) {}
  };
}
